import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, Button, Divider, Paper, Typography } from '@mui/material';
import { Settings } from '../../../interfaces/Settings/Settings';
import { getSettingsByType, upsertSettings } from '../../../api/Settings';
import { SettingsType } from '../../../interfaces/Settings/SettingsType';
import { GenericSettingsValue } from '../../../interfaces/Settings/GenericSettingsValue';
import { getSettingsValue, getUpdatedSettings } from '../../../utils/Settings';
import { Waiting } from '../../../components/Waiting/Waiting';
import { sharedStyles } from '../../../utils/Style';
import { SettingsRow } from '../../../components/Settings/SettingsRow';
import { SettingsValueType } from '../../../interfaces/Settings/SettingsValueType';

const orderReminderKeys = [
  'order_reminder_notification',
  'order_reminder_notification_interval_days',
  'order_reminder_notification_search_period_days',
  'order_reminder_notification_subject',
  'order_reminder_notification_body',
];

export const OrderReminderSettings = () => {
  const [settings, setSettings] = useState<Settings[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshSettings = () => {
    setLoading(true);
    getSettingsByType(SettingsType.MAIL_NOTIFICATION)
      .then((s) => setSettings(s))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshSettings();
  }, []);

  const handleChange = useCallback(
    (key: string, newValue: GenericSettingsValue) =>
      setSettings(
        getUpdatedSettings(
          SettingsType.MAIL_NOTIFICATION,
          key,
          settings,
          newValue,
        ),
      ),
    [settings],
  );

  const handleSave = () => {
    setLoading(true);
    upsertSettings(settings.filter((s) => orderReminderKeys.includes(s.key)))
      .then(() => {
        toast.success('Ayarlar başarıyla kaydedildi');
        refreshSettings();
      })
      .catch((err) => {
        toast.error(err.message);
        setLoading(false);
      });
  };

  return (
    <Paper
      sx={{
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: '95%',
      }}
    >
      <Waiting open={loading} />

      <Typography
        sx={{
          ...sharedStyles.h5,
          fontWeight: 'bold',
        }}
      >
        Sipariş Hatırlatma E-Postası
      </Typography>
      <Divider />

      <SettingsRow
        name='Aktif'
        valueType={SettingsValueType.BOOLEAN}
        value={getSettingsValue(settings, 'order_reminder_notification')}
        onChange={(newValue) => {
          handleChange('order_reminder_notification', newValue);
        }}
      />

      <SettingsRow
        name='Yeniden Sipariş Hatırlatma Süresi'
        valueType={SettingsValueType.NUMBER}
        value={getSettingsValue(
          settings,
          'order_reminder_notification_interval_days',
        )}
        onChange={(newValue) =>
          handleChange('order_reminder_notification_interval_days', newValue)
        }
        minValue={1}
        endAdornment='Gün'
      />

      <SettingsRow
        name='Son Kaç Gün İçinde Sipariş Veren Kullanıcılar Hatırlatma Alsın'
        valueType={SettingsValueType.NUMBER}
        value={getSettingsValue(
          settings,
          'order_reminder_notification_search_period_days',
        )}
        onChange={(newValue) =>
          handleChange(
            'order_reminder_notification_search_period_days',
            newValue,
          )
        }
        minValue={1}
        endAdornment='Gün'
      />

      <SettingsRow
        name='E-Mail Başlığı'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(
          settings,
          'order_reminder_notification_subject',
        )}
        onChange={(newValue) =>
          handleChange('order_reminder_notification_subject', newValue)
        }
        multiline
        placeholder='Bizi unutma!'
      />

      <SettingsRow
        name='E-Mail Mesajı'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'order_reminder_notification_body')}
        onChange={(newValue) =>
          handleChange('order_reminder_notification_body', newValue)
        }
        multiline
        placeholder='Sizi en son mağazamızda gördüğümüzden bu yana biraz zaman geçti. O zamandan beri harika ürünler ekledik, fırsatlar hazırladık ve sizin geri dönmenizi çok isteriz! 💫'
      />

      <Box sx={{ display: 'flex', flexGrow: 1 }} />

      <Box display='flex' justifyContent='flex-end'>
        <Button
          variant='contained'
          onClick={handleSave}
          sx={{ ...sharedStyles.buttonText, mt: 1.5 }}
        >
          Kaydet
        </Button>
      </Box>
    </Paper>
  );
};
